/**
 * 用户护理方案实例服务 (CareSop Instance Service)
 *
 * 包含两套 API：
 * 1. 用户端 API (Assessment 上下文) - /assessments/:id/care_sop
 * 2. Admin API - /api/admin/care_sop_instances
 */

import { userApiClient } from "@/lib/userApiClient";
import apiClient from "@/lib/apiClient";
import type {
  CareSopInstance,
  CareSopInstanceStep,
  CareSopInstanceProgress,
  CareSopInstanceListItem,
  CareSopInstanceFilters,
  CareSopInstanceStatsResponse,
  CareSopInstanceAdminDetail,
} from "@/types/care-sop";

// ============================================================================
// 用户端 API (Assessment 上下文)
// ============================================================================

const ASSESSMENTS_BASE = "/assessments";

export async function getCareSopInstance(
  assessmentId: string,
): Promise<CareSopInstance> {
  return userApiClient.get<CareSopInstance>(
    `${ASSESSMENTS_BASE}/${assessmentId}/care_sop`,
  );
}

export async function toggleStepCompletion(
  assessmentId: string,
  stepIndex: number,
  isCompleted: boolean,
): Promise<{
  step: CareSopInstanceStep;
  progress: CareSopInstanceProgress;
}> {
  return userApiClient.patch(
    `${ASSESSMENTS_BASE}/${assessmentId}/care_sop/steps?step_index=${stepIndex}`,
    { completed: isCompleted },
  );
}

export async function completeCareSop(assessmentId: string): Promise<{
  status: string;
  message: string;
}> {
  return userApiClient.post(
    `${ASSESSMENTS_BASE}/${assessmentId}/care_sop/complete`,
  );
}

export async function abandonCareSop(
  assessmentId: string,
  reason?: string,
): Promise<{
  status: string;
  message: string;
}> {
  return userApiClient.post(
    `${ASSESSMENTS_BASE}/${assessmentId}/care_sop/abandon`,
    reason ? { reason } : undefined,
  );
}

// ============================================================================
// Admin API (后台管理)
// ============================================================================

const ADMIN_BASE = "/api/admin/care_sop_instances";

export async function fetchCareSopInstances(
  filters: CareSopInstanceFilters = {},
): Promise<{
  data: CareSopInstanceListItem[];
  meta: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_count: number;
  };
}> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== "all") {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  const url = queryString ? `${ADMIN_BASE}?${queryString}` : ADMIN_BASE;

  const response = await apiClient<{
    success: boolean;
    data: CareSopInstanceListItem[];
    meta: {
      current_page: number;
      per_page: number;
      total_pages: number;
      total_count: number;
    };
  }>(url, { disableAutoToast: true, returnFullResponse: true });

  return response;
}

export async function fetchCareSopInstanceStats(): Promise<
  CareSopInstanceStatsResponse["data"]
> {
  const response = await apiClient<CareSopInstanceStatsResponse>(
    `${ADMIN_BASE}/stats`,
    { disableAutoToast: true, returnFullResponse: true },
  );
  return response.data;
}

export async function fetchCareSopInstance(
  id: number,
): Promise<CareSopInstanceAdminDetail> {
  return apiClient<CareSopInstanceAdminDetail>(`${ADMIN_BASE}/${id}`);
}

export async function updateCareSopInstanceStatus(
  id: number,
  status: "active" | "completed" | "abandoned",
): Promise<CareSopInstanceAdminDetail> {
  return apiClient<CareSopInstanceAdminDetail>(`${ADMIN_BASE}/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function markCareSopInstanceCompleted(
  id: number,
): Promise<CareSopInstanceAdminDetail> {
  return apiClient<CareSopInstanceAdminDetail>(
    `${ADMIN_BASE}/${id}/mark_completed`,
    {
      method: "POST",
    },
  );
}

export async function terminateCareSopInstance(
  id: number,
  reason?: string,
): Promise<CareSopInstanceAdminDetail> {
  return apiClient<CareSopInstanceAdminDetail>(
    `${ADMIN_BASE}/${id}/terminate`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function batchUpdateCareSopInstanceStatus(
  ids: number[],
  status: "active" | "completed" | "abandoned",
): Promise<{ success: boolean; message: string }> {
  return apiClient<{ success: boolean; message: string }>(
    `${ADMIN_BASE}/batch_update_status`,
    {
      method: "POST",
      body: JSON.stringify({ ids, status }),
    },
  );
}

export async function exportCareSopInstances(
  options: { ids?: number[]; status?: string } = {},
): Promise<Blob> {
  const params = new URLSearchParams();
  if (options.ids?.length) {
    params.append("ids", options.ids.join(","));
  }
  if (options.status) {
    params.append("status", options.status);
  }
  const queryString = params.toString();
  const url = `${ADMIN_BASE}/export?${queryString}`;
  const token = localStorage.getItem("admin_access_token");
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("导出失败");
  return response.blob();
}

export default {
  getCareSopInstance,
  toggleStepCompletion,
  completeCareSop,
  abandonCareSop,
  fetchCareSopInstances,
  fetchCareSopInstanceStats,
  updateCareSopInstanceStatus,
  markCareSopInstanceCompleted,
  terminateCareSopInstance,
  batchUpdateCareSopInstanceStatus,
  exportCareSopInstances,
};
